import React, { useRef } from "react";
import { User, FlashcardCollection, RecallCollection } from "../types";
import { FaDownload, FaUpload, FaTrash, FaTimes, FaSignOutAlt, FaCalendarAlt, FaChartLine, FaClock } from "react-icons/fa";

interface ProfileProps {
  user: User | null;
  onLogout: () => void;
  onClose: () => void;
  collections: FlashcardCollection[];
  recallSheets: RecallCollection[];
  onImportData: (newCols: FlashcardCollection[], newSheets: RecallCollection[]) => void;
  onClearData: () => void;
}

export const Profile: React.FC<ProfileProps> = ({
  user,
  onLogout,
  onClose,
  collections,
  recallSheets,
  onImportData,
  onClearData
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!user) return null;

  const handleExport = () => {
    const dataStr = JSON.stringify({ collections, recallSheets }, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `scoresmart_backup_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        if (json.collections && Array.isArray(json.collections)) {
          onImportData(json.collections, json.recallSheets || []);
          alert("Backup restored successfully!");
          onClose();
        } else {
          alert("Invalid backup file format.");
        }
      } catch (err) {
        alert("Failed to parse file.");
      }
    };
    reader.readAsText(file);
  };

  // Calculate last session time
  const getLastSession = () => {
    if (user.created_at) {
      const createdDate = new Date(user.created_at);
      const now = new Date();
      const diffMs = now.getTime() - createdDate.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return "Today";
      if (diffDays === 1) return "Yesterday";
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      return `${Math.floor(diffDays / 30)} months ago`;
    }
    return "Unknown";
  };

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "700px", maxHeight: "90vh", overflowY: "auto" }}>

        {/* HEADER */}
        <div className="profile-header">
          <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: "10px" }}>
            My Profile
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="profile-content">
          {/* USER INFO CARD */}
          <div style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: "16px",
            padding: "30px",
            color: "white",
            marginBottom: "25px",
            boxShadow: "0 8px 16px rgba(102, 126, 234, 0.3)"
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                  border: "3px solid rgba(255, 255, 255, 0.3)",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "2rem",
                  fontWeight: "bold",
                  flexShrink: 0
                }}
              >
                {user.email?.charAt(0).toUpperCase() || "U"}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: "0 0 8px", fontSize: "1.5rem" }}>
                  {user.user_metadata?.full_name || "User"}
                </h3>
                <p style={{ margin: "0 0 8px", opacity: 0.9, fontSize: "0.95rem" }}>{user.email}</p>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", opacity: 0.8, fontSize: "0.85rem" }}>
                  <FaClock size={12} />
                  <span>Last active: {getLastSession()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* STATS GRID */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "15px", marginBottom: "25px" }}>
            <div style={{
              background: "#f0fdf4",
              border: "1px solid #86efac",
              borderRadius: "12px",
              padding: "20px",
              textAlign: "center"
            }}>
              <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#15803d", marginBottom: "5px" }}>
                {collections.length}
              </div>
              <div style={{ color: "#166534", fontSize: "0.9rem" }}>Flashcard Decks</div>
            </div>
            <div style={{
              background: "#eff6ff",
              border: "1px solid #93c5fd",
              borderRadius: "12px",
              padding: "20px",
              textAlign: "center"
            }}>
              <div style={{ fontSize: "2rem", fontWeight: "bold", color: "#1e40af", marginBottom: "5px" }}>
                {recallSheets.length}
              </div>
              <div style={{ color: "#1e3a8a", fontSize: "0.9rem" }}>Recall Sheets</div>
            </div>
          </div>

          {/* REVISION CALENDAR - COMING SOON */}
          <div style={{
            background: "white",
            border: "2px dashed #cbd5e1",
            borderRadius: "12px",
            padding: "25px",
            marginBottom: "20px",
            position: "relative",
            overflow: "hidden"
          }}>
            <div style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "#fbbf24",
              color: "#78350f",
              padding: "4px 12px",
              borderRadius: "20px",
              fontSize: "0.75rem",
              fontWeight: "700"
            }}>
              COMING SOON
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px" }}>
              <FaCalendarAlt size={24} color="#8b5cf6" />
              <h4 style={{ margin: 0, color: "#334155" }}>Revision Calendar</h4>
            </div>
            <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem" }}>
              Track your daily revision streaks, set study goals, and visualize your learning journey with an interactive calendar.
            </p>
            <div style={{
              marginTop: "15px",
              display: "grid",
              gridTemplateColumns: "repeat(7, 1fr)",
              gap: "8px"
            }}>
              {[...Array(14)].map((_, i) => (
                <div key={i} style={{
                  aspectRatio: "1",
                  background: i % 3 === 0 ? "#e0e7ff" : "#f1f5f9",
                  borderRadius: "6px",
                  opacity: 0.5
                }} />
              ))}
            </div>
          </div>

          {/* PERFORMANCE ANALYTICS - COMING SOON */}
          <div style={{
            background: "white",
            border: "2px dashed #cbd5e1",
            borderRadius: "12px",
            padding: "25px",
            marginBottom: "25px",
            position: "relative",
            overflow: "hidden"
          }}>
            <div style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "#fbbf24",
              color: "#78350f",
              padding: "4px 12px",
              borderRadius: "20px",
              fontSize: "0.75rem",
              fontWeight: "700"
            }}>
              COMING SOON
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px" }}>
              <FaChartLine size={24} color="#10b981" />
              <h4 style={{ margin: 0, color: "#334155" }}>Performance Analytics</h4>
            </div>
            <p style={{ margin: 0, color: "#64748b", fontSize: "0.9rem", marginBottom: "15px" }}>
              Detailed insights into your study patterns, retention rates, and progress over time with beautiful charts and graphs.
            </p>
            <div style={{
              height: "120px",
              background: "linear-gradient(to top, #e0e7ff 0%, #f1f5f9 100%)",
              borderRadius: "8px",
              opacity: 0.5,
              position: "relative"
            }}>
              <div style={{
                position: "absolute",
                bottom: "20px",
                left: "20px",
                right: "20px",
                height: "60px",
                background: "linear-gradient(to right, #8b5cf6, #ec4899, #f59e0b)",
                borderRadius: "4px",
                opacity: 0.6
              }} />
            </div>
          </div>

          <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "20px 0" }} />

          {/* DATA MANAGEMENT SECTION */}
          <h4 style={{ color: "#334155", marginBottom: "15px" }}>Data Management</h4>

          <div style={{ display: "grid", gap: "10px" }}>
            <button
              onClick={handleExport}
              className="secondary-btn"
              style={{ justifyContent: "center", width: "100%" }}
            >
              <FaDownload /> Export Backup
            </button>

            <button
              onClick={() => fileInputRef.current?.click()}
              className="secondary-btn"
              style={{ justifyContent: "center", width: "100%" }}
            >
              <FaUpload /> Import Backup
            </button>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".json"
              onChange={handleFileChange}
            />

            <button
              onClick={() => {
                if (window.confirm("Are you sure? This will delete all local data.")) {
                  onClearData();
                }
              }}
              className="secondary-btn"
              style={{
                justifyContent: "center",
                width: "100%",
                color: "#ef4444",
                borderColor: "#fee2e2",
                background: "#fef2f2"
              }}
            >
              <FaTrash /> Clear Local Data
            </button>
          </div>

          <hr style={{ border: "none", borderTop: "1px solid #e2e8f0", margin: "20px 0" }} />

          {/* LOGOUT */}
          <button
            className="logout-btn"
            onClick={onLogout}
            style={{
              width: "100%",
              padding: "12px",
              background: "#ef4444",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              fontSize: "1rem",
              fontWeight: "600"
            }}
          >
            <FaSignOutAlt /> Log Out
          </button>
        </div>
      </div>
    </div>
  );
};